import { useEffect, useState } from 'react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useMonthlyNetReceived, useSP500Data } from '../hooks/useDashboardData';
import 'chart.js/auto';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const OfferingPerformanceSimple = () => {
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalDeposits, setTotalDeposits] = useState(0);

  // Use React Query for cached data (lightning fast!)
  const { data: monthlyNetReceivedData, isLoading: isMonthlyNetReceivedLoading } = useMonthlyNetReceived();
  const { data: sp500DataFromQuery = [], isLoading: isSP500Loading } = useSP500Data();
  
  console.log('=== React Query Data ===');
  console.log('monthlyNetReceivedData:', monthlyNetReceivedData);
  console.log('isMonthlyNetReceivedLoading:', isMonthlyNetReceivedLoading);
  
  const monthlyRemittances = monthlyNetReceivedData?.monthly_net_received || [];
  const sp500Data = sp500DataFromQuery;
  
  console.log('Extracted monthlyRemittances:', monthlyRemittances);
  console.log('Extracted sp500Data length:', sp500Data?.length);

  useEffect(() => {
    fetchInvestments();
    fetchDeposits();
  }, []);

  const fetchDeposits = async () => {
    try {
      const response = await api.get('/dashboard');
      setTotalDeposits(response.data.deposits || 0);
    } catch (error) {
      console.error('Error fetching deposits:', error);
    }
  };

  // S&P 500 data now loaded via React Query hook (cached!)

  const fetchInvestments = async () => {
    try {
      const response = await api.get('/investments');
      console.log('Loaded investments:', response.data.length);
      setInvestments(response.data);
    } catch (error) {
      console.error('Error fetching investments:', error);
      toast.error('Failed to load investment data');
    } finally {
      setLoading(false);
    }
  };

  // No filtering - show all investments
  const filteredInvestments = investments;

  // Helper function to calculate actual cash received (from remittances - all time)
  const calculateNetReceived = () => {
    console.log('Monthly Remittances Data:', monthlyRemittances);
    console.log('Monthly Remittances Length:', monthlyRemittances?.length);
    const total = monthlyRemittances.reduce((sum, month) => sum + month.amount, 0);
    console.log('Calculated Net Received Total:', total);
    return total;
  };

  const netReceivedFromRemittances = calculateNetReceived();
  console.log('Final netReceivedFromRemittances:', netReceivedFromRemittances);

  // Use total deposits from dashboard (all time)
  const effectiveDeposits = totalDeposits;

  // Calculate Aging Summary (Status Distribution)
  const getAgingSummaryData = () => {
    const summary = {};
    filteredInvestments.forEach(inv => {
      const status = inv['Status'] || 'Unknown';
      const price = parseFloat((inv['Purchase Price'] || '0').toString().replace(/[$,]/g, '')) || 0;
      summary[status] = (summary[status] || 0) + price;
    });

    const labels = Object.keys(summary);
    const data = Object.values(summary);
    
    console.log('Aging Summary - Labels:', labels);
    console.log('Aging Summary - Data:', data);

    // Ensure we have data
    if (labels.length === 0) {
      return {
        labels: ['No Data'],
        datasets: [{
          label: 'Purchase Price by Status',
          data: [0],
          backgroundColor: ['rgba(139, 92, 246, 0.8)'],
          borderColor: ['rgb(139, 92, 246)'],
          borderWidth: 2
        }]
      };
    }

    return {
      labels,
      datasets: [{
        label: 'Purchase Price by Status',
        data,
        backgroundColor: [
          'rgba(139, 92, 246, 0.9)',  // Purple
          'rgba(59, 130, 246, 0.9)',  // Blue
          'rgba(16, 185, 129, 0.9)',  // Green
          'rgba(245, 158, 11, 0.9)',  // Orange
          'rgba(239, 68, 68, 0.9)',   // Red
        ],
        borderColor: [
          'rgb(139, 92, 246)',
          'rgb(59, 130, 246)',
          'rgb(16, 185, 129)',
          'rgb(245, 158, 11)',
          'rgb(239, 68, 68)',
        ],
        borderWidth: 2
      }]
    };
  };

  // Calculate Delinquency Status (Current, Paid In Full, Delinquent)
  const getStatusData = () => {
    const statuses = {};
    filteredInvestments.forEach(inv => {
      const status = inv['Status'] || 'Unknown';
      statuses[status] = (statuses[status] || 0) + 1;
    });

    return {
      labels: Object.keys(statuses),
      datasets: [{
        label: 'Number of Investments',
        data: Object.values(statuses),
        backgroundColor: [
          'rgba(139, 92, 246, 0.8)',
          'rgba(59, 130, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(236, 72, 153, 0.8)',
        ],
        borderColor: '#fff',
        borderWidth: 3
      }]
    };
  };

  // Calculate Delinquencies
  const getDelinquenciesData = () => {
    const statusCounts = {
      'Current': 0,
      'Paid In Full': 0,
      'Delinquent': 0
    };

    filteredInvestments.forEach(inv => {
      const status = inv['Status'] || 'Unknown';
      const price = parseFloat((inv['Purchase Price'] || '0').toString().replace(/[$,]/g, '')) || 0;
      
      if (status === 'Current') {
        statusCounts['Current'] += price;
      } else if (status === 'Paid In Full') {
        statusCounts['Paid In Full'] += price;
      } else {
        // Anything that's not Current or Paid In Full is Delinquent
        statusCounts['Delinquent'] += price;
      }
    });

    const totalDelinquent = statusCounts['Delinquent'];
    const totalPurchasePrice = filteredInvestments.reduce((sum, inv) => {
      return sum + (parseFloat((inv['Purchase Price'] || '0').toString().replace(/[$,]/g, '')) || 0);
    }, 0);
    
    // Calculate delinquency percentage
    const delinquencyPercentage = totalPurchasePrice > 0 ? ((totalDelinquent / totalPurchasePrice) * 100).toFixed(2) : 0;

    return {
      data: {
        labels: Object.keys(statusCounts),
        datasets: [{
          data: Object.values(statusCounts),
          backgroundColor: [
            'rgba(16, 185, 129, 0.8)', // Green for Current
            'rgba(59, 130, 246, 0.8)', // Blue for Paid In Full
            'rgba(239, 68, 68, 0.8)',  // Red for Delinquent
          ],
          borderColor: '#fff',
          borderWidth: 3
        }]
      },
      delinquencyPercentage
    };
  };

  // Term Distribution
  const getTermData = () => {
    const terms = {};
    filteredInvestments.forEach(inv => {
      const term = inv['Term (mo)'] || 'Unknown';
      terms[term] = (terms[term] || 0) + 1;
    });

    const sortedTerms = Object.keys(terms).sort((a, b) => parseInt(a) - parseInt(b));
    
    return {
      labels: sortedTerms,
      datasets: [{
        label: 'Number of Investments',
        data: sortedTerms.map(t => terms[t]),
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 2
      }]
    };
  };

  // Principal by Industry
  const getPrincipalByIndustryData = () => {
    const industries = {};
    filteredInvestments.forEach(inv => {
      const industry = inv['Industry'] || 'Unknown';
      const principal = parseFloat((inv['Purchase Price'] || '0').toString().replace(/[$,]/g, '')) || 0;
      industries[industry] = (industries[industry] || 0) + principal;
    });

    const sortedIndustries = Object.entries(industries)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    return {
      labels: sortedIndustries.map(i => i[0]),
      datasets: [{
        label: 'Total Principal',
        data: sortedIndustries.map(i => i[1]),
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderColor: 'rgb(16, 185, 129)',
        borderWidth: 2
      }]
    };
  };

  // Payment Frequency Mix
  const getPaymentFrequencyMix = () => {
    const freqData = {};
    
    filteredInvestments.forEach(inv => {
      const freq = inv['Payment Frequency'] || 'Unknown';
      if (!freqData[freq]) freqData[freq] = 0;
      freqData[freq] += parseCurrency(inv['Net RTR']);
    });

    return {
      labels: Object.keys(freqData),
      datasets: [{
        data: Object.values(freqData),
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(251, 191, 36, 0.8)',
          'rgba(168, 85, 247, 0.8)',
          'rgba(236, 72, 153, 0.8)'
        ],
        borderColor: '#fff',
        borderWidth: 2
      }]
    };
  };

  // Helper function to parse currency
  const parseCurrency = (value) => {
    if (!value) return 0;
    return parseFloat(value.toString().replace(/[$,]/g, '')) || 0;
  };

  // Principal by Factor Rate
  const getPrincipalByFactorRateData = () => {
    const factorRanges = {
      '1.0-1.1': 0,
      '1.1-1.2': 0,
      '1.2-1.3': 0,
      '1.3-1.4': 0,
      '1.4-1.5': 0,
      '1.5-1.6': 0,
      '1.6+': 0
    };

    filteredInvestments.forEach(inv => {
      const factorRate = parseFloat(inv['Factor Rate']) || 0;
      const principal = parseFloat((inv['Purchase Price'] || '0').toString().replace(/[$,]/g, '')) || 0;

      if (factorRate >= 1.0 && factorRate < 1.1) factorRanges['1.0-1.1'] += principal;
      else if (factorRate >= 1.1 && factorRate < 1.2) factorRanges['1.1-1.2'] += principal;
      else if (factorRate >= 1.2 && factorRate < 1.3) factorRanges['1.2-1.3'] += principal;
      else if (factorRate >= 1.3 && factorRate < 1.4) factorRanges['1.3-1.4'] += principal;
      else if (factorRate >= 1.4 && factorRate < 1.5) factorRanges['1.4-1.5'] += principal;
      else if (factorRate >= 1.5 && factorRate < 1.6) factorRanges['1.5-1.6'] += principal;
      else if (factorRate >= 1.6) factorRanges['1.6+'] += principal;
    });

    return {
      labels: Object.keys(factorRanges),
      datasets: [{
        label: 'Total Principal',
        data: Object.values(factorRanges),
        backgroundColor: 'rgba(245, 158, 11, 0.8)',
        borderColor: 'rgb(245, 158, 11)',
        borderWidth: 2
      }]
    };
  };

  // Investment vs Returns Timeline - Using Funded Date for Invested, Cleared On for Net Received
  const getInvestmentReturnsTimeline = () => {
    const monthlyData = {};
    
    // Process investments by Funded Date for Invested amount
    filteredInvestments.forEach(inv => {
      const date = new Date(inv['Funded Date'] || inv['Created Date']);
      if (isNaN(date)) return;
      
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const purchasePrice = parseFloat((inv['Purchase Price'] || '0').toString().replace(/[$,]/g, '')) || 0;
      const netRTR = parseFloat((inv['Net RTR'] || '0').toString().replace(/[$,]/g, '')) || 0;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { 
          invested: 0, 
          returned: 0,
          netReceived: 0,
          count: 0 
        };
      }
      monthlyData[monthKey].invested += purchasePrice;
      monthlyData[monthKey].returned += netRTR;
      monthlyData[monthKey].count += 1;
    });

    // Add monthly remittances data from backend (already aggregated by Cleared On date)
    monthlyRemittances.forEach(monthData => {
      const monthKey = monthData.month;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { 
          invested: 0, 
          returned: 0,
          netReceived: 0,
          count: 0 
        };
      }
      monthlyData[monthKey].netReceived += monthData.amount;
    });

    const sortedMonths = Object.keys(monthlyData).sort(); // Show ALL months
    
    // Calculate cumulative values (running totals)
    let cumulativeInvested = 0;
    let cumulativeNetReceived = 0;
    
    const cumulativeData = sortedMonths.map(month => {
      cumulativeInvested += monthlyData[month].invested;
      cumulativeNetReceived += monthlyData[month].netReceived;
      
      // Calculate profit: Net Received - Invested
      const cumulativeProfit = cumulativeNetReceived - cumulativeInvested;
      
      return {
        invested: cumulativeInvested,
        netReceived: cumulativeNetReceived,
        profit: cumulativeProfit
      };
    });

    return {
      labels: sortedMonths.map(m => {
        const [year, month] = m.split('-');
        return `${month}/${year.slice(2)}`;
      }),
      datasets: [
        {
          label: 'Cumulative Invested Capital',
          data: cumulativeData.map(d => d.invested),
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          tension: 0.4,
          fill: false,
          borderWidth: 3,
          pointRadius: 0,
          pointHoverRadius: 5
        },
        {
          label: 'Cumulative Net Received',
          data: cumulativeData.map(d => d.netReceived),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          fill: false,
          borderWidth: 3,
          pointRadius: 0,
          pointHoverRadius: 5
        },
        {
          label: 'Cumulative Profit',
          data: cumulativeData.map(d => d.profit),
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          tension: 0.4,
          fill: false,
          borderWidth: 3,
          pointRadius: 0,
          pointHoverRadius: 5
        }
      ]
    };
  };

  // S&P 500 Comparison Chart - Weekly with Real Data (all time)
  const getSPComparison = () => {
    if (sp500Data.length === 0 || monthlyRemittances.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Use all S&P data (no filtering)
    const filteredSP500 = sp500Data;

    // Get all weeks from S&P data
    let allWeeks = filteredSP500.map(sp => sp.date);
    
    // IMPORTANT: Extend to include all months from remittances data
    // This ensures chart shows November even if S&P data stops at October
    const lastRemittanceMonth = monthlyRemittances[monthlyRemittances.length - 1]?.month;
    if (lastRemittanceMonth) {
      const lastRemittanceDate = new Date(lastRemittanceMonth + '-01');
      const lastSPDate = new Date(allWeeks[allWeeks.length - 1]);
      
      // If remittances data extends beyond S&P data, add those months
      if (lastRemittanceDate > lastSPDate) {
        const additionalWeeks = [];
        let currentDate = new Date(lastSPDate);
        currentDate.setDate(currentDate.getDate() + 7); // Start from next week
        
        while (currentDate <= lastRemittanceDate) {
          additionalWeeks.push(currentDate.toISOString().split('T')[0]);
          currentDate.setDate(currentDate.getDate() + 7);
        }
        
        allWeeks = [...allWeeks, ...additionalWeeks];
      }
    }
    
    // Build weekly data from actual remittances (cash received)
    const weeklyNetReceived = {};
    
    // Use all monthly remittances
    monthlyRemittances.forEach(monthData => {
      const monthDate = new Date(monthData.month + '-01');
      
      // Find the closest week in our S&P data
      const closestWeek = allWeeks.find(week => {
        const weekDate = new Date(week);
        return weekDate >= monthDate;
      });
      
      if (closestWeek) {
        weeklyNetReceived[closestWeek] = (weeklyNetReceived[closestWeek] || 0) + monthData.amount;
      }
    });

    // effectiveDeposits is now calculated outside this function

    // Build portfolio account value: Start at deposits, grow proportionally to Net Received
    // Formula: account_value = start_base + cum_rem × ((end_target - start_base) / total_rem)
    const portfolioValues = [];
    let cumulativeNetReceived = 0;
    
    // Starting base (initial deposits)
    const startBase = effectiveDeposits > 100 ? effectiveDeposits : totalDeposits;
    
    // End target (final Net Received)
    const endTarget = netReceivedFromRemittances;
    
    // Total remittances
    const totalRemittances = netReceivedFromRemittances;
    
    // Growth factor: (end_target - start_base) / total_remittances
    const growthFactor = totalRemittances > 0 ? (endTarget - startBase) / totalRemittances : 0;
    
    allWeeks.forEach((week, index) => {
      // Add net received for this week
      if (weeklyNetReceived[week]) {
        cumulativeNetReceived += weeklyNetReceived[week];
      }
      
      // Calculate account value: start_base + cum_rem × growth_factor
      const accountValue = startBase + (cumulativeNetReceived * growthFactor);
      
      portfolioValues.push(accountValue);
    });

    // S&P 500 total account value: Start at deposits, grow based on S&P 500 performance
    const sp500StartValue = filteredSP500.length > 0 ? filteredSP500[0].close : 4000;
    const baseDepositsForSP = effectiveDeposits > 100 ? effectiveDeposits : totalDeposits;
    
    // Map S&P 500 data by date for quick lookup
    const sp500ByDate = {};
    filteredSP500.forEach(item => {
      sp500ByDate[item.date] = item.close;
    });
    
    // Build S&P 500 values for ALL weeks (including extended ones)
    const lastSPValue = filteredSP500[filteredSP500.length - 1]?.close || 4000;
    const sp500AccountValues = allWeeks.map(week => {
      // Use actual S&P data if available, otherwise use last known value (for extended weeks)
      const spValue = sp500ByDate[week] || lastSPValue;
      return baseDepositsForSP * (spValue / sp500StartValue);
    });

    // Smooth both curves using linear interpolation
    const smoothPortfolio = portfolioValues.map((val, idx, arr) => {
      if (idx === 0 || idx === arr.length - 1) return val;
      return (arr[idx - 1] * 0.25 + val * 0.5 + arr[idx + 1] * 0.25);
    });
    
    const smoothSP500 = sp500AccountValues.map((val, idx, arr) => {
      if (idx === 0 || idx === arr.length - 1) return val;
      return (arr[idx - 1] * 0.25 + val * 0.5 + arr[idx + 1] * 0.25);
    });

    // Create labels with November explicitly included
    const allLabels = allWeeks.map(date => {
      const d = new Date(date);
      return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear().toString().slice(2)}`;
    });
    
    // Log for verification
    console.log('Portfolio Chart - Total weeks:', allWeeks.length);
    console.log('Last 3 labels:', allLabels.slice(-3));
    
    return {
      labels: allLabels,
      datasets: [
        {
          label: 'Portfolio Account Value',
          data: smoothPortfolio,
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          tension: 0.4,
          fill: false,
          borderWidth: 3,
          pointRadius: 0,
          pointHoverRadius: 8,
          pointBackgroundColor: 'rgb(16, 185, 129)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          borderCapStyle: 'round',
          borderJoinStyle: 'round'
        },
        {
          label: 'S&P 500 Account Value',
          data: smoothSP500,
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          tension: 0.4,
          fill: false,
          borderWidth: 3,
          pointRadius: 0,
          pointHoverRadius: 8,
          pointBackgroundColor: 'rgb(239, 68, 68)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          borderCapStyle: 'round',
          borderJoinStyle: 'round'
        }
      ]
    };
  };

  // FICO Score Distribution
  const getFICOData = () => {
    const ficoCounts = {};

    filteredInvestments.forEach(inv => {
      const fico = inv['FICO Range'] || 'Unknown';
      if (!ficoCounts[fico]) ficoCounts[fico] = 0;
      ficoCounts[fico] += 1;
    });

    // Get top 5 FICO scores by count (matching FICO Mix Over Time)
    const sortedFicos = Object.entries(ficoCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    const labels = sortedFicos.map(([fico]) => fico);
    const data = sortedFicos.map(([, count]) => count);

    const colors = [
      'rgba(16, 185, 129, 0.8)',
      'rgba(34, 197, 94, 0.8)',
      'rgba(251, 191, 36, 0.8)',
      'rgba(251, 146, 60, 0.8)',
      'rgba(239, 68, 68, 0.8)'
    ];

    return {
      labels,
      datasets: [{
        data,
        backgroundColor: colors,
        borderColor: '#fff',
        borderWidth: 3
      }]
    };
  };


  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 10,
          font: {
            size: 11
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += '$' + context.parsed.y.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
            }
            return label;
          }
        }
      }
    },
    layout: {
      padding: {
        top: 5,
        bottom: 5,
        left: 5,
        right: 5
      }
    }
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      point: {
        radius: 0,
        hoverRadius: 5
      },
      line: {
        tension: 0.4
      }
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 10,
          font: {
            size: 11
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            const value = context.parsed.y;
            if (value !== null) {
              const formattedValue = Math.abs(value).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
              label += value >= 0 ? '$' + formattedValue : '-$' + formattedValue;
            }
            return label;
          }
        }
      },
      // Custom plugin to always show last label (November)
      afterDatasetsDraw: (chart) => {
        const ctx = chart.ctx;
        const xAxis = chart.scales.x;
        const yAxis = chart.scales.y;
        
        // Draw last label manually if not visible
        if (chart.data.labels && chart.data.labels.length > 0) {
          const lastLabel = chart.data.labels[chart.data.labels.length - 1];
          const lastPixel = xAxis.getPixelForValue(chart.data.labels.length - 1);
          
          ctx.save();
          ctx.fillStyle = '#475569';
          ctx.font = '10px Arial';
          ctx.textAlign = 'center';
          ctx.translate(lastPixel, xAxis.bottom + 15);
          ctx.rotate(-0.785); // 45 degrees
          ctx.fillText(lastLabel, 0, 0);
          ctx.restore();
        }
      }
    },
    scales: {
      y: {
        grid: {
          display: false
        },
        ticks: {
          callback: function(value) {
            return '$' + (value/1000).toFixed(0) + 'k';
          },
          font: {
            size: 10
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 9
          },
          maxRotation: 45,
          minRotation: 45,
          autoSkip: false,
          maxTicksLimit: 25
        }
      }
    },
    layout: {
      padding: {
        top: 5,
        bottom: 5,
        left: 5,
        right: 5
      }
    }
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          padding: 8,
          font: {
            size: 11
          },
          boxWidth: 12
        }
      }
    },
    layout: {
      padding: {
        top: 5,
        bottom: 5,
        left: 5,
        right: 5
      }
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Loading Indicator with Spinner */}
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-20 w-20 border-b-4 border-blue-600 mb-4"></div>
            <h3 className="text-2xl font-semibold text-slate-700 mb-2">Loading Performance Analytics...</h3>
            <p className="text-slate-500">Analyzing your portfolio and generating charts</p>
          </div>
        </div>
        
        {/* Skeleton Charts */}
        <div className="grid grid-cols-1 gap-6">
          <div className="bg-white rounded-xl p-6 shadow-sm animate-pulse">
            <div className="h-6 bg-slate-200 rounded w-64 mb-4"></div>
            <div className="h-80 bg-slate-100 rounded"></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[1, 2].map((i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm animate-pulse">
                <div className="h-6 bg-slate-200 rounded w-48 mb-4"></div>
                <div className="h-64 bg-slate-100 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const delinquencies = getDelinquenciesData();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4 mb-4">
        <h1 className="text-2xl font-bold text-slate-900">Offering Performance</h1>
        <div className="text-sm font-semibold text-slate-900 bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
          <span className="text-blue-600">All Time</span>: {filteredInvestments.length.toLocaleString()} investments
        </div>
      </div>

      {/* Portfolio vs S&P 500 Comparison - Chart Left, Stats Right */}
      <div className="mb-6">
        <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
          {/* Title */}
          <div className="flex items-center mb-4">
            <h3 className="text-lg font-bold text-slate-900">Portfolio vs S&P 500 - Total Account Value</h3>
            <span className="ml-2 text-slate-400 cursor-help text-xl" title="Comparison of total account value over time. Both start at $1M invested. Portfolio shows deposits + actual returns received. S&P 500 shows hypothetical account value if invested in index.">ⓘ</span>
          </div>

          {/* Chart and Stats Side by Side */}
          <div className="flex gap-6">
            {/* Chart on Left - 70% */}
            <div className="flex-1" style={{ minWidth: 0 }}>
              <div style={{ height: '450px' }}>
                <Line data={getSPComparison()} options={{
                  ...lineChartOptions,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: false,
                      grid: {
                        display: false
                      },
                      ticks: {
                        callback: function(value) {
                          if (value >= 1000000) {
                            return '$' + (value/1000000).toFixed(2) + 'M';
                          } else if (value >= 1000) {
                            return '$' + (value/1000).toFixed(1) + 'k';
                          } else if (value < 0) {
                            return '-$' + (Math.abs(value)/1000).toFixed(1) + 'k';
                          } else {
                            return '$' + value.toFixed(2);
                          }
                        },
                        font: { size: 11 }
                      }
                    },
                    x: {
                      grid: {
                        display: false
                      },
                      ticks: {
                        font: { size: 11 }
                      }
                    }
                  }
                }} />
              </div>
            </div>

            {/* Stats on Right - 30% */}
            <div className="w-80 flex flex-col justify-center space-y-6">
              {/* Account Values */}
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="text-xs text-slate-500 uppercase tracking-wide mb-2">Starting Capital</div>
                  <div className="text-2xl font-bold text-slate-900">
                    ${(effectiveDeposits > 100 ? effectiveDeposits : totalDeposits).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}
                  </div>
                </div>

                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="text-xs text-red-600 uppercase tracking-wide mb-2">S&P 500 Account Value</div>
                  <div className="text-2xl font-bold text-red-600">
                    {sp500Data.length > 0 ? 
                      `$${((effectiveDeposits > 100 ? effectiveDeposits : totalDeposits) * (sp500Data[sp500Data.length - 1].close / sp500Data[0].close)).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}` 
                      : '$0'}
                  </div>
                  <div className="text-sm text-red-600 mt-2">
                    {sp500Data.length > 0 ? (((sp500Data[sp500Data.length - 1].close / sp500Data[0].close) - 1) * 100).toFixed(2) : '0.00'}% Growth
                  </div>
                  <div className="text-xs text-red-700 mt-2 font-semibold">
                    {(() => {
                      if (sp500Data.length === 0) return '0.00% Yearly Return';
                      const startDate = new Date(sp500Data[0].date);
                      const endDate = new Date(sp500Data[sp500Data.length - 1].date);
                      const years = (endDate - startDate) / (1000 * 60 * 60 * 24 * 365.25);
                      const totalReturn = (sp500Data[sp500Data.length - 1].close / sp500Data[0].close) - 1;
                      const annualizedReturn = years > 0 ? (Math.pow(1 + totalReturn, 1 / years) - 1) * 100 : 0;
                      return `${annualizedReturn.toFixed(2)}% Yearly Return`;
                    })()}
                  </div>
                </div>

                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="text-xs text-green-600 uppercase tracking-wide mb-2">Your Account Value</div>
                  <div className="text-2xl font-bold text-green-600">
                    ${netReceivedFromRemittances.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}
                  </div>
                  <div className="text-sm text-green-600 mt-2">
                    {(() => {
                      const baseAmount = effectiveDeposits > 100 ? effectiveDeposits : totalDeposits;
                      const returns = effectiveDeposits > 100 ? (netReceivedFromRemittances - effectiveDeposits) : netReceivedFromRemittances;
                      return baseAmount > 0 ? ((returns / baseAmount) * 100).toFixed(2) : '0.00';
                    })()}% Growth
                  </div>
                  <div className="text-xs text-green-700 mt-2 font-semibold">
                    {(() => {
                      if (monthlyRemittances.length === 0) return '0.00% Yearly Return';
                      // Get first and last remittance dates to calculate time period
                      const firstMonth = monthlyRemittances[0].month;
                      const lastMonth = monthlyRemittances[monthlyRemittances.length - 1].month;
                      const startDate = new Date(firstMonth + '-01');
                      const endDate = new Date(lastMonth + '-01');
                      const years = (endDate - startDate) / (1000 * 60 * 60 * 24 * 365.25);
                      const baseAmount = effectiveDeposits > 100 ? effectiveDeposits : totalDeposits;
                      const totalReturn = baseAmount > 0 ? (netReceivedFromRemittances - baseAmount) / baseAmount : 0;
                      const annualizedReturn = years > 0 ? (Math.pow(1 + totalReturn, 1 / years) - 1) * 100 : 0;
                      return `${annualizedReturn.toFixed(2)}% Yearly Return`;
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Second Row: Industry Exposure and Delinquency Pipeline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Industry Exposure (Top 10) */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center mb-3">
            <h3 className="text-base font-semibold text-slate-900">Industry Exposure (Top 10)</h3>
            <span className="ml-2 text-slate-400 cursor-help text-lg" title="Shows which industries your invested capital is distributed across. Helps assess sector diversification and concentration risk">ⓘ</span>
          </div>
          <div style={{ height: '240px' }}>
            <Bar data={getPrincipalByIndustryData()} options={chartOptions} />
          </div>
        </div>

        {/* Delinquency Pipeline (Aging Summary) */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center mb-3">
            <h3 className="text-base font-semibold text-slate-900">Delinquency Pipeline</h3>
            <span className="ml-2 text-slate-400 cursor-help text-lg" title="Distribution of investments by aging status. Shows Current (0-30 days), 30-60, 60-90, 90-120, and 120+ days past due">ⓘ</span>
          </div>
          <div style={{ height: '240px' }}>
            <Bar data={getAgingSummaryData()} options={chartOptions} />
          </div>
        </div>
      </div>

      {/* Third Row: FICO Score Distribution and Payment Frequency */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* FICO Score Distribution */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center mb-3">
            <h3 className="text-base font-semibold text-slate-900">FICO Score Distribution</h3>
            <span className="ml-2 text-slate-400 cursor-help text-lg" title="Credit scores of borrowers/merchants receiving funding. Higher FICO scores generally indicate lower risk">ⓘ</span>
          </div>
          <div style={{ height: '240px' }} className="flex items-center justify-center">
            <Pie data={getFICOData()} options={pieOptions} />
          </div>
        </div>

        {/* Payment Frequency Mix */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center mb-3">
            <h3 className="text-base font-semibold text-slate-900">Payment Frequency</h3>
            <span className="ml-2 text-slate-400 cursor-help text-lg" title="Distribution of investments by payment frequency (Daily, Weekly, Monthly, etc.)">ⓘ</span>
          </div>
          <div style={{ height: '240px' }} className="flex items-center justify-center">
            <Pie data={getPaymentFrequencyMix()} options={pieOptions} />
          </div>
        </div>
      </div>

    </div>
  );
};

export default OfferingPerformanceSimple;
